/**
 * What Daily Overview may say about where a driver has got to.
 *
 * The screen reads the dashboard's `/bookings`, which hands back whole
 * booking documents — `driver_status` among them (BookingSchema: `state`
 * 1 on the way, 2 at pickup, 3 passenger on board, 4 dropped off,
 * 5 no-show, plus the time of the tap). Nothing had to be added to the API
 * for any of this; the field was already arriving and nothing on the screen
 * was reading it.
 *
 * What `/bookings` does NOT send is the dispatcher board's `attention`.
 * That one is computed per row by the portal API (ph-core's
 * `tripAttention`, portalDispatcherViews.helper) and only for a LIVE board,
 * and it never reaches this endpoint. So the KEY is worked out here, and
 * only the key: `attentionFor` is a deliberate twin of ph-core's
 * `tripAttention` — same five-minute grace, same order, same answers — and
 * everything downstream of it is the board's own. The words and tones come
 * from `attentionOf`, the step the driver last tapped from `currentStep`,
 * the minutes from `lateBy`, the strip itself from `ProgressStrip`. The
 * office therefore reads the same steps, in the same English, as the
 * dispatcher looking at the same trip — which is the whole point of showing
 * it here rather than sending someone to the other screen.
 *
 * If the two definitions of "late" ever drift apart, this file is the copy
 * to fix: ph-core's is the one the dispatcher board and its counts obey.
 *
 * ONE THING THE CLOCK CANNOT KNOW is whether anybody was ever going to tap
 * anything. Almost no Pickup Hungary driver has a portal login yet, and a
 * driver without one never writes a `driver_status` — so every one of their
 * trips looked like a trip nobody had started, growing more late by the
 * minute until an early-afternoon airport run read "445 min late" purely
 * because its pickup had been seven and a half hours ago. The API now says
 * which drivers hold a live login (`driver.has_app_link`, set once per
 * response — ph-core's portalAppLink.helper).
 *
 * SO LATENESS IS ONLY EVER INFERRED FROM A DRIVER ON THE APP — the owner's
 * "if driver is not using the app he can't be late". Past the pickup with
 * no tap, a trip is `late` only when its driver's `has_app_link` is TRUE;
 * FALSE answers `no_app` (we know why it is silent); absent — the API did
 * not look it up — answers '', as does a trip with no driver at all, where
 * there is nobody whose silence could mean anything ("1314 min late" on a
 * trip with no driver named). That gap is still flagged, as "No driver
 * named" (`needsDriver`), never as lateness. Both twins answer the same.
 *
 * AN EXPERIENCE IS NEVER LATE, for the same reason. It is often run by a
 * guide who never opens the driver app, so "nobody has tapped since the
 * pickup" says nothing about it — "296 min late" on a tour that ran
 * perfectly well. Both twins answer '' there instead of `late` or `no_app`;
 * a tap that WAS made (at the pickup, a no-show) still counts.
 */
import { attentionOf, currentStep, lateBy } from '../Portal/Dispatcher/dispatcherFormat';
import { isNoShow } from '../Portal/portalFormat';
import { EXPENSE_ROLE, isAssignableOperator } from '../../Utils/operatorType';

/**
 * How long past its pickup time a trip nobody has started may sit before
 * the office is told about it. ph-core's `LATE_AFTER_MINUTES`, same value:
 * five minutes is a driver held up at a barrier, not a driver who is not
 * coming.
 */
export const LATE_AFTER_MINUTES = 5;

/** The booking statuses the office sets when somebody did not turn up. */
const NO_SHOW_STATUSES = ['Driver No-Show', 'Passenger No-Show'];

/** Statuses that end a trip: nothing about them is still late. */
const CLOSED_STATUSES = ['Completed', 'Cancelled'];

/**
 * The driver this trip's progress is about, or null: the legacy
 * single-operator field, else a `driver` role in the multi-operator array.
 *
 * Lead first, exactly as the API ranks them — `driver_status` belongs to
 * the booking's lead assignment and the portal refuses a progress event
 * from any other slot, so "who would be tapping" has one answer per trip.
 */
export const progressDriver = (trip) => trip?.driver
  || trip?.operators?.find((entry) => entry.role === 'driver' && entry.driver)?.driver
  || null;

/**
 * Everybody assigned to this booking, one entry per slot:
 * `{ role, operatorId, operator, driverId, driver, car }`.
 *
 * `operators[]` when it has entries, else the legacy single
 * operator/driver/car fields — the reading every assignment column in the
 * dashboard does inline (OrdersTable, OrderDetailsModal). Once `operators[]`
 * exists the legacy fields are only a mirror of its first driver entry
 * (ph-core's `syncOperatorsToLegacyFields`), so reading both would put that
 * driver on the booking twice. A legacy booking with nobody on it has no
 * entries.
 *
 * Cost buckets are left in: they ARE on the booking. A screen that wants
 * only the people doing the work reads `crewOf`.
 */
export const assignmentsOf = (trip) => {
  if (trip?.operators?.length) {
    return trip.operators.map((entry) => ({
      role: entry.role,
      operatorId: entry.operator_id || '',
      operator: entry.operator || null,
      driverId: entry.driver_id || '',
      driver: entry.driver || null,
      car: entry.car || null,
    }));
  }

  if (!trip?.operator_id && !trip?.operator_driver_id) return [];

  return [{
    role: 'driver',
    operatorId: trip.operator_id || '',
    operator: trip.operator || null,
    driverId: trip.operator_driver_id || '',
    driver: trip.driver || null,
    car: trip.car || null,
  }];
};

/**
 * The people doing the work: assignments to a supplier, not to a cost.
 *
 * Either signal is enough. The cost role (`expense`, since 2026-11-03) says
 * it on the entry, for a supplier too when a receipt filed it on a trip it
 * is not on. The record's type catches a cost account still stored under a
 * crew role — every bucket until the stored `assistant` entries are
 * converted.
 */
export const crewOf = (trip) => assignmentsOf(trip)
  .filter((entry) => entry.operatorId
    && entry.role !== EXPENSE_ROLE
    && isAssignableOperator(entry.operator));

/**
 * Is there a driver on this booking at all?
 *
 * The one reading of it on this screen: the card's "No driver" warning and
 * the slot counts both come through here, so they cannot disagree about
 * what "assigned" means on a multi-operator booking.
 */
export const hasDriver = (trip) => Boolean(progressDriver(trip));

/**
 * Is there a driver here who could NOT be reporting progress — somebody
 * assigned, with no live portal login?
 *
 * Only an explicit `false` counts. An absent `has_app_link` means the API
 * did not look it up (an older deployment, a failed read), and guessing
 * "no app" there would put the sentence on every row of the day.
 */
export const driverIsOffApp = (trip) => progressDriver(trip)?.has_app_link === false;

/**
 * `needs_driver` as the dispatcher API sets it — the twin of ph-core's
 * `slotNeedsDriver` (portalViews.helper), asked of the whole booking: one
 * going ahead with nobody to drive it. A trip that is not Confirmed is not a
 * gap — it has been cancelled, completed or is still a request.
 *
 * AN EXPERIENCE NEEDS NO DRIVER. It is often run by a guide, so one with
 * anybody on it (`crewOf`) is staffed; one with NOBODY on it is still the
 * gap it always was. "Anybody" is read off `operators[]` through `crewOf`,
 * never the legacy `operator_id`: on an experience only a driver entry
 * syncs to that field, so a guide-run one has it null.
 */
export const needsDriver = (trip) => trip?.status === 'Confirmed' && !hasDriver(trip)
  && !(trip.booking_type === 'experience' && crewOf(trip).length > 0);

/**
 * The flags the office is meant to DO something about. `no_app` is not one
 * of them: it explains why a row has no progress, and nearly every driver
 * is in that state, so counting it would turn "3 need attention" into a
 * count of the day. The twin of ph-core's `ACTIONABLE_ATTENTION`.
 */
const ACTIONABLE = ['no_show', 'waiting', 'late'];

/**
 * Has this trip's pickup gone by, grace included? The point past which a
 * trip nobody has tapped on stops reading as on time: `late` or `no_app`
 * where a driver's app link says which (`attentionFor`), else "progress not
 * visible" (dayTimeline's `barLook`). False when there is no pickup time to
 * measure.
 */
export const pastPickup = (trip, now = new Date()) => {
  const pickup = trip?.date_datetime ? new Date(trip.date_datetime).getTime() : NaN;

  return Number.isFinite(pickup) && now.getTime() > pickup + LATE_AFTER_MINUTES * 60 * 1000;
};

/**
 * `'no_show' | 'waiting' | 'late' | 'no_app' | ''` — the twin of ph-core's
 * `tripAttention`, read off the same `driver_status.state`.
 *
 * Nothing is flagged on a finished or cancelled trip, and — because the
 * only test left is against the clock — nothing at all is flagged on a trip
 * that has not come round yet. A day the office is looking at in advance
 * stays calm by construction, with no separate "is this board live" switch
 * to get wrong.
 *
 * `late` and `no_app` are inferred from silence, so they need somebody
 * whose silence means something: a driver whose app link is known, on a
 * trip that is not an experience (see the top of this file). Real taps are
 * read on every trip.
 */
export const attentionFor = (trip, now = new Date()) => {
  const state = Number(trip?.driver_status?.state);

  if (state === 5 || NO_SHOW_STATUSES.includes(trip?.status)) return 'no_show';

  // Done is done: a completed trip is not late, however it ran.
  if (CLOSED_STATUSES.includes(trip?.status) || state === 4) return '';

  // Standing at the pickup with nobody in the car yet.
  if (state === 2) return 'waiting';

  // Under way with the passenger on board is the trip going to plan.
  if (state === 3) return '';

  // Everything from here is inferred from nobody having tapped — which on an
  // experience is no news at all.
  if (trip?.booking_type === 'experience') return '';

  if (!pastPickup(trip, now)) return '';

  // Late only from a driver who could have tapped and has not. Nobody on it,
  // or a driver the API did not look up, says nothing — the missing driver
  // has its own warning ("No driver named") beside this one.
  if (driverIsOffApp(trip)) return 'no_app';

  return progressDriver(trip)?.has_app_link === true ? 'late' : '';
};

/**
 * Everything one row says about its driver, in one read:
 *
 *   `step`      what the driver last tapped, in the board's English
 *   `attention` `{ key, label, tone }` when the office should act, else null
 *   `late`      '12 min late', on a late trip only
 *   `showsStrip` false where a filled strip would lie — a cancelled trip,
 *               and a no-show, whose state 5 `progressIndex` rounds down to
 *               a full four bars. The same rule the two portal trip screens
 *               use, so a strip never appears in one place and not another.
 */
export const driverProgress = (trip, now = new Date()) => {
  const key = attentionFor(trip, now);
  // `attentionOf` reads the field the dispatcher API sends; handing it the
  // key we just worked out is what keeps the labels and tones single-sourced.
  const attention = attentionOf({ attention: key });

  return {
    step: currentStep(trip),
    attention,
    late: key === 'late' ? lateBy({ ...trip, attention: key }, now.getTime()) : '',
    showsStrip: trip?.status !== 'Cancelled' && !isNoShow(trip),
  };
};

/**
 * A row the office should do something about: flagged (late, waiting or a
 * no-show) OR going ahead with nobody on it.
 *
 * The twin of ph-core's `rowNeedsAttention`, same two tests joined the same
 * way — the hour headings count with this, so the count above a group and
 * the rows under it can never disagree. `no_app` is flagged but not
 * counted: see ACTIONABLE.
 */
export const rowNeedsAttention = (trip, now = new Date()) => ACTIONABLE.includes(
  attentionFor(trip, now),
) || needsDriver(trip);
